import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormAutocomplete } from '@ipfabric/ui-components/FormAutocomplete/FormAutocomplete';
import { FormSegmentedButton } from '@ipfabric/ui-components/FormSegmentedButton/FormSegmentedButton';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  type ManualLinkLayer,
  type PublicGlobalManualLink,
  type PublicSnapshotManualLink,
  type TableRequestBody,
  asNonEmptyString,
} from '@ipfabric/utils';
import { useApiTableAutocomplete } from 'components/FormikTypeaheadInput/useApiTableAutocomplete';
import { useMemo } from 'react';
import {
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { generateGuid } from 'utils/guid';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import type { ApiRequestParams } from 'utils/request/ApiRequestParams';
import type { PublicManualLink } from './model/ManualLinksModel';

type Props<TManualLink extends PublicManualLink> = {
  editedItem: TManualLink | 'new';
  form: UseFormReturn<{ manualLinks: PublicManualLink[] }>;
  isSnapshotSpecific: boolean;
  onAddItem: (item: PublicManualLink) => void;
  onClose: () => void;
  onUpdateItem: (index: number, item: PublicManualLink) => void;
};

interface DeviceSnOption {
  hostname: string | null;
  sn: string;
  ts?: number; // needed only for sorting inside the query
}

interface InterfaceOption {
  intName: string;
  sn: string;
}

// Remove duplicates from the list of devices.
// The list is sorted by the timestamp of the last discovery, so the first occurrence is the most recent one.
const getUniqueDevicesBySn = <T extends DeviceSnOption>(
  devices: T[] | null,
): T[] => {
  const seenSerialNumbers = new Set<string>();

  return (devices ?? []).filter((device) => {
    const isUnique = !seenSerialNumbers.has(device.sn);
    if (isUnique) {
      seenSerialNumbers.add(device.sn);
    }
    return isUnique;
  });
};

export const ManualLinkForm = <TManualLink extends PublicManualLink>(
  props: Props<TManualLink>,
) => {
  const methods = useCustomForm<PublicManualLink>({
    defaultValues:
      props.editedItem !== 'new'
        ? { ...props.editedItem }
        : {
            // Temporarily generated 'id' is used on the UI, as it is necessary for operations such as deleting an entry in the table. The backend ignores it and assigns its own 'id' afterward.
            id: asNonEmptyString(generateGuid()),
            srcDeviceSn: '',
            srcInterface: '',
            dstDeviceSn: '',
            dstInterface: '',
            type: 'L1',
          },
  });

  const { control, handleSubmit, formState, setValue } = methods;

  const srcDeviceSn = useWatch({
    control,
    name: 'srcDeviceSn',
  });
  const dstDeviceSn = useWatch({
    control,
    name: 'dstDeviceSn',
  });

  const snapshotId = useSnapshotId();

  const updateManualLink = useStableCallback((data: PublicManualLink) => {
    const links = props.form.getValues().manualLinks;
    const foundIndex = links.findIndex((item) => {
      return item?.id && item.id === data.id;
    });

    if (foundIndex === -1) {
      return;
    }

    if (links[foundIndex]?.settingType !== data.settingType) {
      stumble(
        `Manual link type mismatch, tried to update ${links[foundIndex]!.settingType} to ${data.settingType}`,
      );
    }

    props.onUpdateItem(foundIndex, data);
  });

  const addManualLink = useStableCallback((data: PublicManualLink) => {
    const links = props.form.getValues().manualLinks;
    if (links.length > 0 && links[0]?.settingType !== data.settingType) {
      stumble(
        // Fixed noUncheckedIndexedAccess NIM-19408
        `Pushing manual link of type ${data.settingType} to an array of ${links[0]!.settingType} links`,
      );
    }

    props.onAddItem(data);
  });

  const srcDeviceSnAutocompleteOptions =
    useApiTableAutocomplete<DeviceSnOption>({
      snapshot: false,
      fetchWithEmptyQuery: true,
      createRequest: (
        searchEvent,
      ): ApiRequestParams<TableRequestBody<DeviceSnOption>> => ({
        url: '/tables/inventory/discovery-history',
        data: {
          columns: ['sn', 'hostname'],
          filters: {
            or: [
              { sn: ['like', searchEvent.inputValue] },
              { hostname: ['like', searchEvent.inputValue] },
            ],
          },
          sort: {
            column: 'ts',
            order: 'desc',
          },
        },
      }),
    });

  const distinctSrcDeviceSnOptions = useMemo(
    () => getUniqueDevicesBySn(srcDeviceSnAutocompleteOptions.content),
    [srcDeviceSnAutocompleteOptions.content],
  );

  const dstDeviceSnAutocompleteOptions =
    useApiTableAutocomplete<DeviceSnOption>({
      snapshot: false,
      fetchWithEmptyQuery: true,
      createRequest: (
        searchEvent,
      ): ApiRequestParams<TableRequestBody<DeviceSnOption>> => ({
        url: '/tables/inventory/discovery-history',
        data: {
          columns: ['sn', 'hostname'],
          filters: {
            or: [
              { sn: ['like', searchEvent.inputValue] },
              { hostname: ['like', searchEvent.inputValue] },
            ],
          },
          sort: {
            column: 'ts',
            order: 'desc',
          },
        },
      }),
    });

  const distinctDstDeviceSnOptions = useMemo(
    () => getUniqueDevicesBySn(dstDeviceSnAutocompleteOptions.content),
    [dstDeviceSnAutocompleteOptions.content],
  );

  const srcInterfaceAutocompleteOptions =
    useApiTableAutocomplete<InterfaceOption>({
      snapshot: false,
      fetchWithEmptyQuery: true,
      createRequest: (
        searchEvent,
      ): ApiRequestParams<TableRequestBody<InterfaceOption>> => ({
        url: '/tables/inventory/interfaces',
        data: {
          columns: ['sn', 'intName'],
          filters: {
            and: [
              { sn: ['eq', srcDeviceSn] },
              { intName: ['like', searchEvent.inputValue] },
            ],
          },
          snapshot: snapshotId || undefined,
        },
      }),
    });

  const dstInterfaceAutocompleteOptions =
    useApiTableAutocomplete<InterfaceOption>({
      snapshot: false,
      fetchWithEmptyQuery: true,
      createRequest: (
        searchEvent,
      ): ApiRequestParams<TableRequestBody<InterfaceOption>> => ({
        url: '/tables/inventory/interfaces',
        data: {
          columns: ['sn', 'intName'],
          filters: {
            and: [
              { sn: ['eq', dstDeviceSn] },
              { intName: ['like', searchEvent.inputValue] },
            ],
          },
          snapshot: snapshotId || undefined,
        },
      }),
    });

  const onSubmit: SubmitHandler<PublicManualLink> = (values) => {
    const data: PublicSnapshotManualLink | PublicGlobalManualLink =
      props.isSnapshotSpecific
        ? {
            ...values,
            settingType: 'snapshot',
            status: null,
            statusCode: null,
          }
        : {
            ...values,
            settingType: 'global',
          };
    if (props.editedItem === 'new') {
      addManualLink(data);
    } else {
      updateManualLink(data);
    }
    props.onClose();
  };

  const interfaceLabelTooltip =
    'Suggestions for valid interfaces can only be provided when a snapshot is loaded and a recognized device is selected.';

  return (
    <FormProvider {...methods}>
      <SidePanel
        title={
          props.editedItem === 'new' ? 'New manual link' : 'Edit manual link'
        }
        closeOnBackdropClick={false}
        onClose={props.onClose}
        footer={({ closeModal }) => (
          <div className='flex gap-xs'>
            <Button
              type='submit'
              dataTestId='manual-links-save'
              disabled={!formState.isDirty}
              loading={formState.isSubmitting}
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              className='flex-1'
              icon='fa-save'
            >
              {props.editedItem === 'new' ? 'Add link' : 'Save changes'}
            </Button>
            <Button variant='outline' onClick={closeModal} className='flex-1'>
              Close
            </Button>
          </div>
        )}
      >
        <form>
          <Card className='flex flex-col gap p-sm'>
            <Message
              variant='plain'
              type='info'
              title='Sub-interfaces are not supported'
              message='Manual links must not be created on subinterfaces and aggregated interfaces. Ensure all link configurations are applied only to physical interfaces.'
            />
            <FormSegmentedButton.Single<PublicManualLink, ManualLinkLayer>
              control={control}
              name='type'
              label='Link type'
              rules={{
                required: 'Link type is required',
              }}
            >
              <SegmentButton value='L1' className='flex-1'>
                Layer 1
              </SegmentButton>
              <SegmentButton value='L2' className='flex-1'>
                Layer 2
              </SegmentButton>
            </FormSegmentedButton.Single>
            <div className='grid gap grid-cols-[1fr_110px]'>
              <FormAutocomplete.Record<PublicManualLink, DeviceSnOption>
                control={control}
                getOptionLabel={({ sn, hostname }) =>
                  `${sn} / ${hostname ?? 'unknown hostname'}`
                }
                isOptionEqualToValue={(option, value) => option.sn === value}
                label='Device (from)'
                loading={srcDeviceSnAutocompleteOptions.loading}
                name='srcDeviceSn'
                onChange={() => setValue('srcInterface', '')}
                onSearch={srcDeviceSnAutocompleteOptions.search}
                optionValueName='sn'
                options={distinctSrcDeviceSnOptions}
                placeholder='Serial number or hostname'
                rules={{
                  required: 'Device is required',
                }}
              />

              {snapshotId && srcDeviceSn ? (
                <FormAutocomplete.Record<PublicManualLink, InterfaceOption>
                  control={control}
                  label='Interface'
                  labelTooltip={interfaceLabelTooltip}
                  loading={srcInterfaceAutocompleteOptions.loading}
                  name='srcInterface'
                  onSearch={srcInterfaceAutocompleteOptions.search}
                  optionValueName='intName'
                  options={srcInterfaceAutocompleteOptions.content ?? []}
                  rules={{
                    required: 'Interface is required',
                  }}
                />
              ) : (
                <FormTextField
                  control={control}
                  name='srcInterface'
                  type='text'
                  label='Interface'
                  labelTooltip={interfaceLabelTooltip}
                  onClear={() => {
                    setValue('srcInterface', '');
                  }}
                  rules={{
                    required: 'Interface is required',
                  }}
                />
              )}

              <FormAutocomplete.Record<PublicManualLink, DeviceSnOption>
                getOptionLabel={({ sn, hostname }) =>
                  `${sn} / ${hostname ?? 'unknown hostname'}`
                }
                isOptionEqualToValue={(option, value) => option.sn === value}
                label='Device (to)'
                loading={dstDeviceSnAutocompleteOptions.loading}
                name='dstDeviceSn'
                onChange={() => setValue('dstInterface', '')}
                onSearch={dstDeviceSnAutocompleteOptions.search}
                optionValueName='sn'
                options={distinctDstDeviceSnOptions}
                placeholder='Serial number or hostname'
                rules={{
                  required: 'Device is required',
                }}
              />

              {snapshotId && dstDeviceSn ? (
                <FormAutocomplete.Record<PublicManualLink, InterfaceOption>
                  control={control}
                  label='Interface'
                  labelTooltip={interfaceLabelTooltip}
                  loading={dstInterfaceAutocompleteOptions.loading}
                  name='dstInterface'
                  onSearch={dstInterfaceAutocompleteOptions.search}
                  optionValueName='intName'
                  options={dstInterfaceAutocompleteOptions.content ?? []}
                  rules={{
                    required: 'Interface is required',
                  }}
                />
              ) : (
                <FormTextField
                  control={control}
                  name='dstInterface'
                  type='text'
                  label='Interface'
                  labelTooltip={interfaceLabelTooltip}
                  onClear={() => {
                    setValue('dstInterface', '');
                  }}
                  rules={{
                    required: 'Interface is required',
                  }}
                />
              )}
            </div>
          </Card>
        </form>
      </SidePanel>
    </FormProvider>
  );
};
